import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2, TrendingUp, Upload, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  borrarProyeccionCaja,
  cargarProyeccionCaja,
  crearProyeccionCaja,
  editarProyeccionCaja,
  getProyeccionCaja,
  type CargaProyeccionCaja,
  type ProyeccionCaja,
} from "@/lib/api";

type Moneda = "MXN" | "USD";

const num = (n: number) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fechaCorta = (iso: string) => {
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(a, m - 1, d).toLocaleDateString("es-MX", {
    day: "2-digit", month: "short", year: "2-digit",
  });
};

const aNumero = (s: string) => Number(s.replace(/[$,\s]/g, ""));

const aTiempo = (iso: string) => {
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(a, m - 1, d).getTime();
};

const diaAntes = (iso: string) => {
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(a, m - 1, d - 1).toLocaleDateString("en-CA");
};

// Eje Y compacto: 1.2 M / 350 K.
const fmtEje = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)} M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(0)} K`;
  return v.toFixed(0);
};

type PuntoCaja = {
  t: number;
  fecha: string;
  saldo: number;
  ingresos: number;
  egresos: number;
  movs: number;
};

type Borrador = { fecha_pago: string; concepto: string; tipo: string; monto: string };

const NUEVO = -1;

function leerMoneda(): Moneda {
  try {
    return localStorage.getItem("proyeccion-caja-moneda") === "USD" ? "USD" : "MXN";
  } catch {
    return "MXN";
  }
}

/*
 * Operación → Contabilidad → Proyección de Caja. Por moneda: saldo inicial +
 * ingresos/egresos proyectados (Excel o captura) y el saldo de la caja después
 * de cada movimiento, en orden de fecha, hasta el saldo final.
 */
export function ProyeccionCajaPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [moneda, setMonedaState] = useState<Moneda>(leerMoneda);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<CargaProyeccionCaja | null>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const [borrador, setBorrador] = useState<Borrador | null>(null);

  const setMoneda = (m: Moneda) => {
    setMonedaState(m);
    setPrevia(null);
    setArchivo(null);
    setEditando(null);
    setAviso(null);
    try {
      localStorage.setItem("proyeccion-caja-moneda", m);
    } catch {
      /* sin storage */
    }
  };

  const q = useQuery({
    queryKey: ["proyeccion-caja", moneda],
    queryFn: () => getProyeccionCaja(moneda),
  });
  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const saldoInicial = q.data?.saldo_inicial ?? 0;
  const corte = q.data?.corte ?? null;
  const vencidos = corte ? items.filter((p) => p.fecha_pago <= corte).length : 0;

  const invalidar = () => qc.invalidateQueries({ queryKey: ["proyeccion-caja"] });
  const err = (e: unknown, def: string) => setError(e instanceof Error ? e.message : def);

  // Saldo de la caja después de cada movimiento (orden fecha, id; el alta
  // manual nueva no entra hasta guardarla).
  const filas = useMemo(() => {
    let saldo = saldoInicial;
    return items.map((p) => {
      saldo += p.tipo === "Ingreso" ? p.monto : -p.monto;
      return { p, saldo };
    });
  }, [items, saldoInicial]);
  const ingresos = items.filter((p) => p.tipo === "Ingreso").reduce((a, p) => a + p.monto, 0);
  const egresos = items.filter((p) => p.tipo === "Egreso").reduce((a, p) => a + p.monto, 0);
  const saldoFinal = saldoInicial + ingresos - egresos;
  const minimo = filas.reduce<{ saldo: number; fecha: string } | null>(
    (m, f) => (m == null || f.saldo < m.saldo ? { saldo: f.saldo, fecha: f.p.fecha_pago } : m),
    null,
  );

  // Serie para la gráfica: saldo inicial en la fecha de corte (último
  // movimiento bancario; hoy si no hay) y un punto por fecha de pago con el
  // saldo después del último movimiento de ese día, hasta el último pago.
  const serie = useMemo<PuntoCaja[]>(() => {
    if (filas.length === 0) return [];
    const porFecha = new Map<string, PuntoCaja>();
    for (const { p, saldo } of filas) {
      const e = porFecha.get(p.fecha_pago) ?? {
        t: aTiempo(p.fecha_pago), fecha: p.fecha_pago, saldo, ingresos: 0, egresos: 0, movs: 0,
      };
      e.saldo = saldo;
      e.movs += 1;
      if (p.tipo === "Ingreso") e.ingresos += p.monto; else e.egresos += p.monto;
      porFecha.set(p.fecha_pago, e);
    }
    let inicio = corte ?? new Date().toLocaleDateString("en-CA");
    if (filas[0].p.fecha_pago <= inicio) inicio = diaAntes(filas[0].p.fecha_pago);
    return [
      { t: aTiempo(inicio), fecha: inicio, saldo: saldoInicial, ingresos: 0, egresos: 0, movs: 0 },
      ...porFecha.values(),
    ];
  }, [filas, corte, saldoInicial]);
  const puntoMin = serie.length ? serie.reduce((m, p) => (p.saldo < m.saldo ? p : m), serie[0]) : null;

  const carga = useMutation({
    mutationFn: (p: { f: File; modo: "agregar" | "reemplazar"; confirmar: boolean }) =>
      cargarProyeccionCaja(p.f, moneda, p.modo, p.confirmar),
    onSuccess: (r) => {
      setError(null);
      if (r.aplicado) {
        setPrevia(null);
        setArchivo(null);
        setAviso(
          r.modo === "reemplazar"
            ? `Proyección ${moneda} reemplazada: ${r.borrados} borrados, ${r.validos} cargados.`
            : `${r.validos} movimientos agregados a la proyección ${moneda}.`,
        );
        invalidar();
      } else {
        setAviso(null);
        setPrevia(r);
      }
    },
    onError: (e) => err(e, "No se pudo leer el archivo"),
  });

  const guardar = useMutation({
    mutationFn: (p: { id: number; b: Borrador }) => {
      const body = {
        moneda,
        fecha_pago: p.b.fecha_pago,
        concepto: p.b.concepto.trim() || null,
        tipo: p.b.tipo,
        monto: p.b.monto.trim() === "" ? null : aNumero(p.b.monto),
      };
      return p.id === NUEVO ? crearProyeccionCaja(body) : editarProyeccionCaja(p.id, body);
    },
    onSuccess: () => {
      setError(null);
      setEditando(null);
      setBorrador(null);
      invalidar();
    },
    onError: (e) => err(e, "No se pudo guardar"),
  });

  const borrar = useMutation({
    mutationFn: (id: number) => borrarProyeccionCaja(id),
    onSuccess: invalidar,
    onError: (e) => err(e, "No se pudo borrar"),
  });

  const onArchivo = (f: File | null) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    setArchivo(f);
    carga.mutate({ f, modo: "agregar", confirmar: false });
  };

  const empezarNuevo = () => {
    setEditando(NUEVO);
    setBorrador({
      fecha_pago: new Date().toLocaleDateString("en-CA"),
      concepto: "",
      tipo: "Egreso",
      monto: "",
    });
  };

  const editarFila = (p: ProyeccionCaja) => {
    setEditando(p.id);
    setBorrador({ fecha_pago: p.fecha_pago, concepto: p.concepto ?? "", tipo: p.tipo, monto: String(p.monto) });
  };

  const cancelar = () => {
    setEditando(null);
    setBorrador(null);
  };

  const filaEdicion = (id: number) =>
    borrador && (
      <FilaEdicion
        key={id}
        b={borrador}
        onChange={setBorrador}
        ocupado={guardar.isPending}
        onGuardar={() => guardar.mutate({ id, b: borrador })}
        onCancelar={cancelar}
      />
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-500" /> Proyección de Caja
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            El saldo inicial es el <b>Saldo Final de las cuentas</b> de la moneda en Movimientos
            bancarios <b>más sus inversiones</b>; sobre él se suman ingresos y se restan egresos proyectados, con el saldo de la
            caja después de cada movimiento. Sube el Excel con columnas <b>Fecha de Pago | Concepto | Tipo | Monto</b>{" "}
            (se carga en la moneda seleccionada) o captura los movimientos a mano.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border overflow-hidden text-sm" role="group" aria-label="Moneda">
            {(["MXN", "USD"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMoneda(m)}
                className={`px-3 py-2 ${moneda === m ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                Cuenta en {m}
              </button>
            ))}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={(e) => onArchivo(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={empezarNuevo}
            disabled={editando !== null}
            className="inline-flex items-center gap-2 border text-slate-700 rounded px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Agregar
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={carga.isPending}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {carga.isPending ? "Leyendo…" : "Cargar Excel"}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm flex justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {aviso && !previa && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">{aviso}</div>
      )}

      {previa && archivo && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 space-y-3 text-sm">
          <div className="text-slate-700">
            Previa de <b>{archivo.name}</b> → proyección en <b>{moneda}</b>: {previa.validos} movimientos válidos
            {previa.desde && ` (${fechaCorta(previa.desde)} → ${fechaCorta(previa.hasta ?? previa.desde)})`} ·
            ingresos <b className="text-emerald-700">{num(previa.ingresos)}</b> · egresos{" "}
            <b className="text-amber-700">{num(previa.egresos)}</b>
          </div>
          {previa.errores.length > 0 && (
            <div className="text-xs text-red-700 max-h-40 overflow-y-auto border border-red-100 rounded p-2 bg-red-50">
              {previa.errores.length} renglones se descartan:
              {previa.errores.map((e) => (
                <div key={e.renglon}>Renglón {e.renglon}: {e.motivo}</div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={carga.isPending || previa.validos === 0}
              onClick={() => carga.mutate({ f: archivo, modo: "agregar", confirmar: true })}
              className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-1.5 hover:bg-blue-700 disabled:opacity-50"
            >
              <Check className="w-4 h-4" /> Agregar a lo existente ({previa.existentes} + {previa.validos})
            </button>
            {previa.existentes > 0 && (
              <button
                type="button"
                disabled={carga.isPending || previa.validos === 0}
                onClick={() => {
                  if (window.confirm(`Se borrarán los ${previa.existentes} movimientos proyectados en ${moneda} y se cargarán ${previa.validos}. ¿Continuar?`)) {
                    carga.mutate({ f: archivo, modo: "reemplazar", confirmar: true });
                  }
                }}
                className="inline-flex items-center gap-2 border border-amber-300 text-amber-800 rounded px-4 py-1.5 hover:bg-amber-50 disabled:opacity-50"
              >
                Reemplazar todo {moneda} ({previa.existentes} → {previa.validos})
              </button>
            )}
            <button
              type="button"
              onClick={() => { setPrevia(null); setArchivo(null); }}
              className="border text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Resumen: saldo inicial → saldo final */}
      {q.data && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6 text-sm">
          <div className="bg-white border rounded-lg p-3">
            <div className="text-xs text-slate-500">Saldo inicial ({moneda})</div>
            <div className={`text-lg font-semibold tabular-nums mt-1 ${saldoInicial < 0 ? "text-red-600" : "text-slate-900"}`}>
              {num(saldoInicial)}
            </div>
            <Link
              to="/movimientos-bancarios"
              className="text-[11px] text-blue-600 hover:underline"
              title="Saldo Final de las cuentas en esta moneda + sus inversiones (Movimientos bancarios)"
            >
              Saldo final de {q.data.cuentas.join(" + ") || "las cuentas"}
            </Link>
            <div className="text-[11px] text-slate-500 tabular-nums">
              Cuentas {num(q.data.saldo_cuentas)} + inversiones {num(q.data.inversion)}
            </div>
          </div>
          <Kpi label="Ingresos proyectados" valor={ingresos} cls="text-emerald-700" signo="+ " />
          <Kpi label="Egresos proyectados" valor={egresos} cls="text-amber-700" signo="− " />
          <Kpi label={`Saldo final (${moneda})`} valor={saldoFinal} cls={saldoFinal < 0 ? "text-red-600" : "text-slate-900"} />
          <Kpi
            label={minimo ? `Saldo mínimo (${fechaCorta(minimo.fecha)})` : "Saldo mínimo"}
            valor={minimo ? Math.min(minimo.saldo, saldoInicial) : saldoInicial}
            cls={(minimo?.saldo ?? saldoInicial) < 0 ? "text-red-600" : "text-slate-900"}
          />
          {/* Las dos monedas en pesos: MXN + USD × tipo de cambio (Configuración → Tipo de Cambio). */}
          {(() => {
            const tc = q.data.tipo_cambio.mxn_por_usd;
            const { MXN: fMxn, USD: fUsd } = q.data.saldos_finales;
            const total = fMxn + fUsd * tc;
            return (
              <div className={`bg-white rounded-lg p-3 border-[3px] ${total < 0 ? "border-red-600" : "border-emerald-600"}`}>
                <div className="text-xs text-slate-500">Saldo Final Total en MXN</div>
                <div className={`text-lg font-semibold tabular-nums mt-1 ${total < 0 ? "text-red-600" : "text-emerald-700"}`}>
                  {num(total)}
                </div>
                <div className="text-[11px] text-slate-500 tabular-nums leading-snug">
                  {num(fMxn)} MXN + {num(fUsd)} USD ×{" "}
                  <Link to="/tipo-cambio" className="underline hover:text-slate-900" title="Editar en Configuración → Tipo de Cambio">
                    TC {tc.toFixed(2)}
                  </Link>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Evolución de la caja: saldo inicial → saldo después de cada fecha de pago */}
      {q.data && serie.length > 1 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <div className="text-sm font-medium text-slate-800">Evolución de la caja ({moneda})</div>
            <div className="text-xs text-slate-500">
              Saldo inicial al {fechaCorta(serie[0].fecha)} → último pago el {fechaCorta(serie[serie.length - 1].fecha)}
              {puntoMin && puntoMin.saldo < 0 && (
                <span className="ml-2 text-red-600 font-medium">· la caja se vuelve negativa el {fechaCorta(puntoMin.fecha)}</span>
              )}
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={serie} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                <defs>
                  <linearGradient id="caja-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563eb" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 4" vertical={false} />
                <XAxis
                  dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]}
                  tickFormatter={(t: number) => fechaCorta(new Date(t).toLocaleDateString("en-CA"))}
                  tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} minTickGap={40}
                />
                <YAxis
                  tickFormatter={fmtEje} tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false} tickLine={false} width={64}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    const p = payload?.[0]?.payload as PuntoCaja | undefined;
                    if (!active || !p) return null;
                    return (
                      <div className="bg-white border rounded shadow-sm px-3 py-2 text-xs space-y-0.5">
                        <div className="font-medium text-slate-700">{fechaCorta(p.fecha)}</div>
                        <div className={`tabular-nums font-semibold ${p.saldo < 0 ? "text-red-600" : "text-slate-900"}`}>
                          Saldo {num(p.saldo)}
                        </div>
                        {p.movs === 0 ? (
                          <div className="text-slate-500">Saldo inicial (cuentas + inversiones)</div>
                        ) : (
                          <div className="text-slate-500 tabular-nums">
                            {p.movs} mov. · {p.ingresos > 0 && <span className="text-emerald-700">+{num(p.ingresos)} </span>}
                            {p.egresos > 0 && <span className="text-amber-700">−{num(p.egresos)}</span>}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <ReferenceLine y={0} stroke={puntoMin && puntoMin.saldo < 0 ? "#dc2626" : "#94a3b8"} strokeDasharray="4 3" />
                <ReferenceLine
                  y={saldoInicial} stroke="#94a3b8" strokeDasharray="2 4"
                  label={{ value: "Saldo inicial", position: "insideTopLeft", fontSize: 10, fill: "#64748b" }}
                />
                <Area
                  type="stepAfter" dataKey="saldo" stroke="#2563eb" strokeWidth={2}
                  fill="url(#caja-area)" dot={{ r: 2.5, fill: "#2563eb", strokeWidth: 0 }} activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
                {puntoMin && puntoMin.movs > 0 && (
                  <ReferenceDot
                    x={puntoMin.t} y={puntoMin.saldo} r={5}
                    fill={puntoMin.saldo < 0 ? "#dc2626" : "#f59e0b"} stroke="#fff" strokeWidth={1.5}
                    label={{ value: `mín. ${fmtEje(puntoMin.saldo)}`, position: "bottom", fontSize: 10, fill: puntoMin.saldo < 0 ? "#dc2626" : "#b45309" }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {vencidos > 0 && corte && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 text-sm">
          {vencidos} movimientos proyectados tienen fecha ≤ {fechaCorta(corte)} (último movimiento bancario cargado). Si
          ya ocurrieron, ya están en el saldo inicial y aquí se cuentan doble: bórralos o reprográmalos. El{" "}
          <Link to="/reportes/caja" className="underline">Reporte de Caja</Link> no los cuenta.
        </div>
      )}

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">{(q.error as Error).message}</div>
      )}

      {q.data && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2">Fecha de pago</th>
                <th className="px-3 py-2">Concepto</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2 text-right">Ingreso</th>
                <th className="px-3 py-2 text-right">Egreso</th>
                <th className="px-3 py-2 text-right">Saldo actual de la caja</th>
                <th className="px-3 py-2 w-16" />
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50 font-medium">
                <td className="px-3 py-1.5" colSpan={5}>Saldo inicial</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${saldoInicial < 0 ? "text-red-600" : ""}`}>{num(saldoInicial)}</td>
                <td />
              </tr>
              {editando === NUEVO && filaEdicion(NUEVO)}
              {filas.map(({ p, saldo }) =>
                editando === p.id ? (
                  filaEdicion(p.id)
                ) : (
                  <tr key={p.id} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">
                      {fechaCorta(p.fecha_pago)}
                      {corte && p.fecha_pago <= corte && (
                        <span
                          className="ml-1.5 text-[10px] font-medium rounded px-1 py-0.5 bg-amber-100 text-amber-800"
                          title="Fecha ≤ último movimiento bancario: si ya ocurrió, está en Movimientos bancarios y se cuenta doble"
                        >
                          ≤ corte
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{p.concepto ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-1.5">
                      <span
                        className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${
                          p.tipo === "Ingreso" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {p.tipo}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">
                      {p.tipo === "Ingreso" ? num(p.monto) : ""}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-amber-700">
                      {p.tipo === "Egreso" ? num(p.monto) : ""}
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${saldo < 0 ? "text-red-600" : "text-slate-900"}`}>
                      {num(saldo)}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <button
                        type="button"
                        title="Modificar"
                        disabled={editando !== null}
                        onClick={() => editarFila(p)}
                        className="text-slate-400 hover:text-blue-600 disabled:opacity-30 mr-2"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title="Eliminar"
                        disabled={editando !== null}
                        onClick={() => {
                          if (window.confirm(`¿Eliminar "${p.concepto ?? p.tipo}" del ${fechaCorta(p.fecha_pago)}?`)) borrar.mutate(p.id);
                        }}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ),
              )}
              {items.length === 0 && editando !== NUEVO && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Sin movimientos proyectados en {moneda} — carga el Excel o agrega uno.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <tr>
                <td className="px-3 py-2" colSpan={3}>Saldo final · {items.length} movimientos</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{num(ingresos)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-amber-700">{num(egresos)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${saldoFinal < 0 ? "text-red-600" : "text-slate-900"}`}>{num(saldoFinal)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, valor, cls, signo = "" }: { label: string; valor: number; cls: string; signo?: string }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums mt-1 ${cls}`}>{signo}{num(valor)}</div>
    </div>
  );
}

function FilaEdicion({
  b,
  onChange,
  ocupado,
  onGuardar,
  onCancelar,
}: {
  b: Borrador;
  onChange: (b: Borrador) => void;
  ocupado: boolean;
  onGuardar: () => void;
  onCancelar: () => void;
}) {
  const set = (k: keyof Borrador, v: string) => onChange({ ...b, [k]: v });
  const inp = "border rounded px-1.5 py-1 text-sm w-full";
  return (
    <tr
      className="border-t bg-blue-50/60"
      onKeyDown={(e) => {
        if (e.key === "Enter") onGuardar();
        if (e.key === "Escape") onCancelar();
      }}
    >
      <td className="px-2 py-1.5">
        <input type="date" value={b.fecha_pago} onChange={(e) => set("fecha_pago", e.target.value)} className={inp} />
      </td>
      <td className="px-2 py-1.5 min-w-[220px]">
        <input autoFocus value={b.concepto} onChange={(e) => set("concepto", e.target.value)} className={inp} />
      </td>
      <td className="px-2 py-1.5">
        <select value={b.tipo} onChange={(e) => set("tipo", e.target.value)} className={inp}>
          <option>Ingreso</option>
          <option>Egreso</option>
        </select>
      </td>
      <td className="px-2 py-1.5" colSpan={2}>
        <input
          inputMode="decimal"
          placeholder="Monto"
          value={b.monto}
          onChange={(e) => set("monto", e.target.value)}
          className={`${inp} text-right`}
        />
      </td>
      <td className="px-3 py-1.5 text-right text-xs text-slate-400">se recalcula al guardar</td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <button type="button" title="Guardar (Enter)" disabled={ocupado} onClick={onGuardar} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40 mr-2">
          <Check className="w-4 h-4" />
        </button>
        <button type="button" title="Cancelar (Esc)" onClick={onCancelar} className="text-slate-400 hover:text-slate-700">
          <X className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}
